import { Button } from '@/components/ui/button';
import { Edit, Eye, Download, Undo2 } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faDownload, faSparkles, faEllipsisV } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { Badge } from '@/components/ui/badge';
import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Document } from '@/types/document';
import { cleanDateAndTimeObject, renderDate } from '@/../shared/renderDate';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { convertStatus, getStatusVariant } from '../index';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

// Define valid badge variants as a const array for type safety
// DED-943: Added 'taskevidence' variant for Task Evidence documents
const BADGE_VARIANTS = [
  'active', 'inactive', 'deleted', 'secondary', 'policy', 'procedure',
  'form', 'report', 'record', 'manual', 'plan', 'guideline', 'standard',
  'checklist', 'matrix', 'presentation', 'pdf', 'evidence', 'taskevidence', 'image',
  'spreadsheet', 'database', 'code', 'website', 'template', 'hardware',
  'software', 'service', 'personnel', 'documentation', 'other'
] as const;

type BadgeVariant = typeof BADGE_VARIANTS[number];

// Utility to get document type variant for Badge component
const getDocTypeVariant = (type: string): BadgeVariant => {
  if (!type) return 'secondary';

  const lowerType = type.toLowerCase();
  
  // DED-943: Handle 'task evidence' specially - map to 'taskevidence' variant
  if (lowerType === 'task evidence') {
    return 'taskevidence';
  }
  
  const validDocVariants = [
    'policy', 'procedure', 'form', 'report', 'record', 'manual', 'plan',
    'guideline', 'standard', 'checklist', 'matrix', 'presentation', 'pdf',
    'evidence', 'taskevidence', 'image', 'spreadsheet', 'database', 'code', 'website',
    'template', 'hardware', 'software', 'service', 'personnel', 'documentation', 'other'
  ] as const;

  return validDocVariants.includes(lowerType as any) ? lowerType as BadgeVariant : 'secondary';
};

interface DocumentActionsProps {
  onEdit?: (row: Document) => void;
  onView?: (row: Document) => void;
  onDownload?: (row: Document) => void;
  onRecover?: (row: Document) => void;
  onGenerateAITasks?: (row: Document) => void;
  userIdToName: Record<string, string> | ((userId: string) => string);
  viewMode?: 'active' | 'deleted';
  helpData: any;
  isReadOnly?: boolean;
}

export const getDocumentColumns = (onActions: DocumentActionsProps, features?: { aiHarold: boolean; masterControlMapping: boolean }): ColumnDef<Document, any>[] => [
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.documentname']}>Document Name</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'fileName',
    cell: ({ row }) => {
      const original = row.original as any;
      const name = original.displayName || original.fileName;
      return <span className="text-left w-full block" data-force-isms-override="true">{name}</span>;
    },
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.type']}>Type</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'documentType',
    cell: ({ getValue }) => {
      const type = getValue() as string;
      return (
        <div className="text-center w-full block">
          <Badge variant={getDocTypeVariant(type || 'Other') as any}>{type || 'Other'}</Badge>
        </div>
      );
    },
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.uploadedby']}>{onActions.viewMode !== 'deleted' ? 'Uploaded By' : 'Deleted By'}</HelpPopoverHover>
      </span>
    ),
    accessorKey: onActions.viewMode !== 'deleted' ? 'uploadedBy' : 'deletedBy',
    cell: ({ getValue }) => {
      // console.log(`[DocumentLibrary] View mode: ${onActions.viewMode}`)
      const userId = getValue();
      // console.log(`[DocumentLibrary] userId: ${userId}`);
      if (!userId) return <span className="text-center w-full block">Deleted or unknown user</span>;

      let displayName;
      if (typeof onActions.userIdToName === 'function') {
        // If it's a function, call the function
        displayName = onActions.userIdToName(userId) || 'Deleted or unknown user';
      } else {
        // If it's an object, use property access
        displayName = onActions.userIdToName[userId] || 'Deleted or unknown user';
      }

      return <span className="text-center w-full block">{displayName || 'Deleted or unknown user'}</span>;
    },
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.status']}>Status</HelpPopoverHover>
      </span>
    ),
    accessorKey: 'status',
    cell: ({ row }) => {
      const status = row.original.status as string;

      // if document is of type Evidence / Task Evidence then we don't have a next review date
      // DED-943: Task Evidence documents should have no review dates
      // console.log('row.original', row.original);
      const docType = row.original?.documentType?.toLowerCase();
      if (docType === 'evidence' || docType === 'task evidence') {
        return <span className="text-center w-full block">
          <Badge variant={getStatusVariant(convertStatus('Published', null)) as BadgeVariant}>
            <span className="inline-block w-2 h-2 rounded-full mr-1 bg-current opacity-70"></span>
            Published
          </Badge>
        </span>;
      }
      return (
        <div className="text-center w-full">
          <Badge variant={getStatusVariant(convertStatus(status || 'Published', cleanDateAndTimeObject(row.original.nextReviewDate))) as BadgeVariant}>
            <span className="inline-block w-2 h-2 rounded-full mr-1 bg-current opacity-70"></span>
            {convertStatus(status || 'Published', cleanDateAndTimeObject(row.original.nextReviewDate))}
          </Badge>
        </div>
      );
    },
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.nextreviewdate']}>{onActions.viewMode !== 'deleted' ? 'Next Review Date' : 'Deleted Date'}</HelpPopoverHover>
      </span>
    ),
    accessorKey: onActions.viewMode !== 'deleted' ? 'nextReviewDate' : 'deletedAt',
    cell: ({ row }) => {
      const isDeleted = onActions.viewMode === 'deleted' || row.original.status === 'Deleted';
      const date = row.original[!isDeleted ? 'nextReviewDate' : 'deletedAt'];
      let formattedDate;

      // if document is of type Evidence / Task Evidence then we don't have a next review date
      // DED-943: Task Evidence documents should have no review dates
      // console.log('row.original', row.original);
      const docTypeForDate = row.original?.documentType?.toLowerCase();
      if (docTypeForDate === 'evidence' || docTypeForDate === 'task evidence') {
        formattedDate = 'Not Applicable';
        return <span className="text-center w-full block">{formattedDate}</span>;
      }

      if (date) {
        // Use type guards to handle different date formats
        if (typeof date === 'object' && date !== null) {
          // Handle object types
          const dateObj = date as any; // Use any for property access

          if (dateObj.toDate && typeof dateObj.toDate === 'function') {
            // Firestore Timestamp-like object
            formattedDate = renderDate(dateObj.toDate());
          } else if (dateObj.__time__ && typeof dateObj.__time__ === 'string') {
            // Object with __time__ property
            formattedDate = renderDate(new Date(dateObj.__time__));
          } else {
            // Try to use it directly
            try {
              formattedDate = renderDate(dateObj);
            } catch (e) {
              formattedDate = 'Not Set';
            }
          }
        } else if (typeof date === 'string') {
          // Handle string date
          try {
            formattedDate = renderDate(new Date(date));
          } catch (e) {
            formattedDate = date;
          }
        }
      }

      return <span className="text-center w-full block">{formattedDate || '-'}</span>;
    }
  },
  {
    header: () => (
      <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={onActions.helpData?.['table.header.actions']}>Actions</HelpPopoverHover>
      </span>
    ),
    id: 'actions',
    cell: ({ row }) => {
      const isDeleted = onActions.viewMode === 'deleted' || row.original.status === 'Deleted';

      // Calculate number of actions to determine if we need dropdown
      const baseActions = onActions.isReadOnly ? 1 : 2; // Edit/View + View (or just View for read-only)
      const downloadAction = 1; // Always available
      const aiTasksAction = (!onActions.isReadOnly && features?.aiHarold) ? 1 : 0;
      const totalActions = baseActions + downloadAction + aiTasksAction;
      const useDropdown = totalActions > 3;

      return (
        <div id={TOUR_STEP_IDS.DOC_TABLE_ACTION_BUTTONS} className="flex gap-1 justify-center relative">
          {!isDeleted ? (
            <>
              {/* {!onActions.isReadOnly ? (
                <Button variant="ghost" size="icon" onClick={(e) => {
                  e.preventDefault();
                  onActions.onEdit?.(row.original)
                }} title="Edit">
                  <FontAwesomeIcon icon={faEdit} className="h-4 w-4"/>
                </Button>
              ) : (
                <Button variant="ghost" size="icon" onClick={(e) => {
                  e.preventDefault();
                  onActions.onView?.(row.original)
                }} title="View Only">
                  <FontAwesomeIcon icon={faEye} className="h-4 w-4 "/>
                </Button>
              )} */}
              {/* Always show view button in non-read-only mode */}
              <Button variant="ghost" size="icon" onClick={(e) => {
                e.preventDefault();
                onActions.onView?.(row.original)
              }} title="View">
                <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
              </Button>

              {/* Show download directly if no dropdown needed */}
              <Button variant="ghost" size="icon" onClick={(e) => {
                e.preventDefault();
                onActions.onDownload?.(row.original)
              }} title="Download">
                <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
              </Button>

              {/* Show AI Tasks directly if no dropdown needed and feature enabled */}
              {!useDropdown && !onActions.isReadOnly && features.aiHarold && (
                <Button variant="ghost" size="icon" onClick={(e) => {
                  e.preventDefault();
                  onActions.onGenerateAITasks?.(row.original)
                }} title="Suggest Tasks">
                  <FontAwesomeIcon icon={faSparkles} className="h-4 w-4" />
                </Button>
              )}

              {/* Dropdown Menu - only show if we have more than 3 actions */}
              {useDropdown && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" title="More Actions">
                      <FontAwesomeIcon icon={faEllipsisV} className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {/* <DropdownMenuItem onClick={(e) => {
                      e.preventDefault();
                      onActions.onDownload?.(row.original)
                    }}>
                      <FontAwesomeIcon icon={faDownload} className="h-4 w-4 mr-2" />
                      Download
                    </DropdownMenuItem> */}

                    {!onActions.isReadOnly && features.aiHarold && (
                      <DropdownMenuItem onClick={(e) => {
                        e.preventDefault();
                        onActions.onGenerateAITasks?.(row.original)
                      }}>
                        <FontAwesomeIcon icon={faSparkles} className="h-4 w-4 mr-2" />
                        Suggest Tasks
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary/80"
              onClick={(e) => {
                e.preventDefault();
                onActions.onRecover?.(row.original)
              }}
              disabled={onActions.isReadOnly}
            >
              Recover
            </Button>
          )}
        </div>
      );
    },
    enableSorting: false,
  },
];
